import * as React from 'react'
import Dropzone from 'react-dropzone'

import { Box, Text, Tooltip } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import type { Field } from 'factorial-form'
import _ from 'lodash'

import { observer } from 'mobx-react'

import { Authorized } from 'modules/core/components/Authorized'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import DocumentText from 'modules/documents/components/Documents/DocumentUploader/DocumentText'

import styles from './index.module.scss'

type DropzoneProps = React.ComponentProps<typeof Dropzone>

const cx = classNames.bind(styles)
interface Props {
  accept?: DropzoneProps['accept']
  field: Field
  placeholder: string
  customFieldsRender?: () => void
  readonly?: boolean
  renderChildren?: () => JSX.Element | null
  onDropped?: (files: File[]) => void
}

const DocumentUploader = ({
  accept,
  field,
  placeholder,
  customFieldsRender,
  readonly,
  renderChildren,
  onDropped,
}: Props) => {
  const error = !field.isDirty ? field.errors && field.errors[0] : null

  const {
    company: { subscription },
  } = useCurrentSession()

  const className = cx('dropzone', {
    dropzoneInactive: !!readonly,
    wrong: Boolean(error),
  })

  const getName = () => {
    if (!field.value) return null

    if (_.isString(field.value)) return i18n.t('common.document')

    return field.value.name || field.value.filename
  }

  const onDrop = (files: File[]) => {
    field.set(files[0])

    if (onDropped) onDropped(files)
  }

  return (
    <Tooltip attachment="bottom center" type="wrong" size="small" open={Boolean(error)}>
      <Box>
        <>
          <Authorized subscription={subscription}>
            <Dropzone accept={accept} noClick={readonly} onDrop={onDrop}>
              {({ getInputProps, getRootProps, isDragActive, isDragReject }) => (
                <div
                  {...getRootProps()}
                  className={`${className} ${isDragActive && styles.dropzoneActive}`}
                >
                  <input data-testid="dropzone-input" {...getInputProps()} />
                  <DocumentText filename={getName()} placeholder={placeholder} />
                  {accept && isDragReject && (
                    <Text.Normal color="radicalRed1000">
                      {i18n.t('common.upload_document_reject')}
                    </Text.Normal>
                  )}
                  {!!renderChildren && <div className={styles.children}>{renderChildren()}</div>}
                </div>
              )}
            </Dropzone>
          </Authorized>
          {customFieldsRender && customFieldsRender()}
        </>
      </Box>
      <Text.Normal>{error}</Text.Normal>
    </Tooltip>
  )
}

export default observer(DocumentUploader)
